import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type AllianceHub,
  type AllianceHubBusterWeek,
  type AllianceHubGlowUp,
  type AllianceHubHighscore,
  type AllianceHubIronThumbs,
  type AllianceHubMegaExpressWindow,
  type AllianceHubPlayer,
  type AllianceHubSleepingBeauty,
  type AllianceHubStrengthTop,
  type AllianceHubTrainLeader,
  type AllianceHubVsKindRoast,
  type AllianceHubVsWeeklySlackers,
  type AllianceHubWorstParker,
  type AuthUser,
} from '@/lib/api';
import { useAllianceLabel } from '@/lib/useCurrentAlliance';
import {
  downloadPoster,
  PosterCard,
  PosterFrame,
  PosterScaledPreview,
} from '@/components/Poster';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function playerLabel(p: { name: string; displayName: string | null }): string {
  return p.displayName?.trim() || p.name;
}

function formatKillsM(n: number): string {
  return n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '.0');
}

function formatShortDate(iso: string): string {
  // "2026-08-31" → "31.08."
  const d = new Date(iso + 'T00:00:00Z');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.`;
}

function vsWeekLabel(data: AllianceHub): string | null {
  const w = data.vsWeeklySlackers ?? data.vsKindRoast;
  if (!w) return null;
  return `VS-Week ${formatShortDate(w.weekStart)} – ${formatShortDate(w.weekEnd)}`;
}

function formatHours(h: number): string {
  if (h < 48) return `${h}h`;
  const days = Math.floor(h / 24);
  const rest = h % 24;
  return rest === 0 ? `${days}d` : `${days}d ${rest}h`;
}

export default function AdminHallOfFame({ user }: { user: AuthUser }) {
  const canEdit = user.role === 'R5' || user.role === 'R4';
  const q = useQuery({
    queryKey: ['admin-hall-of-fame'],
    queryFn: api.getAdminHallOfFame,
    enabled: canEdit,
  });

  const awardsRef = useRef<HTMLDivElement | null>(null);
  const roastsRef = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState<'awards' | 'roasts' | null>(null);

  if (!canEdit) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-3 p-4">
        <h1 className="text-xl font-semibold">Hall of Fame export</h1>
        <p className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          R4/R5 required.
        </p>
      </div>
    );
  }

  const runExport = async (kind: 'awards' | 'roasts') => {
    const node =
      kind === 'awards' ? awardsRef.current : roastsRef.current;
    if (!node) return;
    setBusy(kind);
    try {
      const iso = q.data?.today ?? new Date().toISOString().slice(0, 10);
      await downloadPoster(node, `foa-${kind}-${iso}.png`);
    } catch (err) {
      console.error(err);
      alert('Export failed. Check console.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4">
      <header>
        <h1 className="text-xl font-semibold">Hall of Fame export</h1>
        <p className="text-sm text-slate-400">
          Live snapshot of the alliance-hub tiles as a portrait 1080×1350
          PNG. Awards (positive) and Roasts (negative) are two separate
          posters.
        </p>
      </header>

      {q.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : q.isError || !q.data ? (
        <div className="text-sm text-rose-300">
          Could not load hall-of-fame data.
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <PosterCard
            title="Awards"
            description="Iron Thumbs · Glow-Up · Buster · Strength Top · Train Leaders."
            busy={busy === 'awards'}
            onExport={() => runExport('awards')}
          >
            <PosterScaledPreview>
              <div ref={awardsRef}>
                <AwardsPoster data={q.data} />
              </div>
            </PosterScaledPreview>
          </PosterCard>

          <PosterCard
            title="Roasts"
            description="Worst Parker · Sleeping Beauties · weekly VS-Slackers · Chat-Ban Hall of Fame."
            busy={busy === 'roasts'}
            onExport={() => runExport('roasts')}
          >
            <PosterScaledPreview>
              <div ref={roastsRef}>
                <RoastsPoster data={q.data} />
              </div>
            </PosterScaledPreview>
          </PosterCard>
        </div>
      )}
    </div>
  );
}

// -------------------- Posters --------------------

function AwardsPoster({ data }: { data: AllianceHub }) {
  const allianceLabel = useAllianceLabel();
  return (
    <PosterFrame
      accent="#22c55e"
      title={`${allianceLabel} · Awards`}
      weekLabel={vsWeekLabel(data) ?? `Week of ${data.weekStart}`}
    >
      <div style={{ display: 'grid', gap: 14, overflow: 'hidden' }}>
        <IronThumbsPosterCard data={data.ironThumbs} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <GlowUpPosterCard data={data.glowUp} />
          <BusterPosterCard data={data.busterWeek} />
        </div>
        <StrengthTopPosterCard data={data.strengthTop} />
        <MegaExpressCalendarWeekPosterCard data={data.megaExpressCalendarWeek} />
        <TrainLeadersPosterCard
          leaders={data.trainLeaders}
          weekStart={data.weekStart}
          weekDescription={data.trainWeekDescription}
        />
      </div>
    </PosterFrame>
  );
}

function RoastsPoster({ data }: { data: AllianceHub }) {
  const allianceLabel = useAllianceLabel();
  return (
    <PosterFrame
      accent="#f43f5e"
      title={`${allianceLabel} · Roasts`}
      weekLabel={vsWeekLabel(data) ?? `Week of ${data.weekStart}`}
    >
      <div style={{ display: 'grid', gap: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <WorstParkerPosterCard data={data.worstParker} />
          <SleepingBeautyPosterCard data={data.sleepingBeauty} />
        </div>
        {data.vsKindRoast && <VsKindRoastPosterCard data={data.vsKindRoast} />}
        <VsWeeklySlackersPosterCard data={data.vsWeeklySlackers} />
        <ChatBanHofPosterCard data={data.chatBans.highscores} />
      </div>
    </PosterFrame>
  );
}

// -------------------- Frame + shared bits --------------------

function PosterCardBox({
  accent,
  label,
  children,
  emphasis,
}: {
  accent: string;
  label: string;
  children: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        border: `2px solid ${accent}66`,
        background: emphasis ? `${accent}18` : 'rgba(15, 23, 42, 0.6)',
        borderRadius: 18,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: accent,
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function BigName({
  player,
  color = '#f8fafc',
}: {
  player: AllianceHubPlayer;
  color?: string;
}) {
  return (
    <div
      style={{
        fontSize: 26,
        fontWeight: 800,
        color,
        overflowWrap: 'anywhere',
        lineHeight: 1.05,
      }}
    >
      {playerLabel(player)}
    </div>
  );
}

function QuipLine({ text, color }: { text: string; color: string }) {
  if (!text) return null;
  return (
    <div
      style={{
        fontSize: 15,
        fontStyle: 'italic',
        color,
        marginTop: 4,
      }}
    >
      …{text}
    </div>
  );
}

// -------------------- Awards cards --------------------

function IronThumbsPosterCard({
  data,
}: {
  data: AllianceHubIronThumbs | null;
}) {
  if (!data || data.top.length === 0) {
    return (
      <PosterCardBox accent="#22c55e" label="Iron Thumbs">
        <div style={{ color: '#94a3b8' }}>No streaks yet.</div>
      </PosterCardBox>
    );
  }
  const [leader, ...rest] = data.top;
  return (
    <PosterCardBox
      accent="#22c55e"
      label="Iron Thumbs · most self-reports · past 7 days"
      emphasis
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <BigName player={leader.player} color="#bbf7d0" />
        <div
          style={{
            fontSize: 34,
            fontWeight: 800,
            color: '#4ade80',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {leader.count}× · +{leader.deltaM.toFixed(2)}M
        </div>
      </div>
      <div style={{ fontSize: 14, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
        avg +{leader.avgM.toFixed(2)}M per report
      </div>
      <QuipLine text={leader.quip} color="#86efac" />
      {rest.length > 0 && (
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          {rest.map((e, i) => (
            <div
              key={e.player.id}
              style={{
                flex: 1,
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid rgba(74, 222, 128, 0.25)',
                borderRadius: 12,
                padding: '10px 14px',
              }}
            >
              <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 700 }}>
                #{i + 2}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>
                {playerLabel(e.player)}
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>
                {e.count}× · +{e.deltaM.toFixed(2)}M
              </div>
            </div>
          ))}
        </div>
      )}
    </PosterCardBox>
  );
}

function GlowUpPosterCard({ data }: { data: AllianceHubGlowUp | null }) {
  if (!data) {
    return (
      <PosterCardBox accent="#818cf8" label="Glow-Up">
        <div style={{ color: '#94a3b8' }}>No qualifying growth.</div>
      </PosterCardBox>
    );
  }
  return (
    <PosterCardBox accent="#818cf8" label={`Glow-Up · last ${data.days}d`} emphasis>
      <BigName player={data.player} color="#c7d2fe" />
      <div
        style={{
          fontSize: 36,
          fontWeight: 800,
          color: '#a5b4fc',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        +{(data.pct * 100).toFixed(1)}%
      </div>
      <div style={{ fontSize: 14, color: '#c7d2fe' }}>
        {data.fromM.toFixed(1)} → {data.toM.toFixed(1)} Mio (+
        {(data.toM - data.fromM).toFixed(1)})
      </div>
      <QuipLine text={data.quip} color="#a5b4fc" />
    </PosterCardBox>
  );
}

function BusterPosterCard({ data }: { data: AllianceHubBusterWeek | null }) {
  if (!data) {
    return (
      <PosterCardBox accent="#fb923c" label="Buster of the week">
        <div style={{ color: '#94a3b8' }}>No buster data.</div>
      </PosterCardBox>
    );
  }
  return (
    <PosterCardBox
      accent="#fb923c"
      label={`Buster · since Sat ${data.saturdayIso}`}
      emphasis
    >
      <BigName player={data.player} color="#fed7aa" />
      <div
        style={{
          fontSize: 36,
          fontWeight: 800,
          color: '#fdba74',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        +{formatKillsM(data.deltaKills)} M
      </div>
      <div style={{ fontSize: 14, color: '#fed7aa' }}>kills</div>
      <QuipLine text={data.quip} color="#fdba74" />
    </PosterCardBox>
  );
}

function MegaExpressCalendarWeekPosterCard({
  data,
}: {
  data: AllianceHubMegaExpressWindow | null;
}) {
  if (!data) {
    return (
      <PosterCardBox accent="#10b981" label="Mega-Express · last week">
        <div style={{ color: '#94a3b8' }}>No Mega-Express data.</div>
      </PosterCardBox>
    );
  }
  return (
    <PosterCardBox
      accent="#10b981"
      label={`Mega-Express · ${data.weekStart} → ${data.weekEnd}`}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 800, color: '#a7f3d0' }}>
          {data.total} bought
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          {data.top.length} buyer{data.top.length === 1 ? '' : 's'}
        </div>
      </div>
      {data.top.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>
          Nobody stepped up.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 4, marginTop: 6 }}>
          {data.top.slice(0, 5).map((r, i) => (
            <div
              key={r.player.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 12,
                fontSize: 15,
              }}
            >
              <span style={{ color: '#f1f5f9', overflowWrap: 'anywhere' }}>
                <span style={{ color: '#64748b', marginRight: 6 }}>
                  {i + 1}.
                </span>
                {playerLabel(r.player)}
              </span>
              <span
                style={{
                  color: '#6ee7b7',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 700,
                }}
              >
                {r.count}×
              </span>
            </div>
          ))}
        </div>
      )}
    </PosterCardBox>
  );
}

function StrengthTopPosterCard({
  data,
}: {
  data: AllianceHubStrengthTop | null;
}) {
  if (!data) return null;
  const cell = (
    label: string,
    entry: NonNullable<AllianceHubStrengthTop['m1']>,
    color: string,
    value: string = `+${entry.deltaM.toFixed(1)} M`,
  ) => (
    <div
      style={{
        flex: 1,
        border: '2px solid rgba(148, 163, 184, 0.15)',
        background: 'rgba(15, 23, 42, 0.6)',
        borderRadius: 16,
        padding: 16,
      }}
    >
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color, fontWeight: 700 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          marginTop: 4,
          overflowWrap: 'anywhere',
          lineHeight: 1.1,
        }}
      >
        {playerLabel(entry.player)}
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
        …{entry.quip}
      </div>
    </div>
  );
  return (
    <PosterCardBox
      accent="#38bdf8"
      label={`Strength push · ${data.periodLabel} · ${data.ranked} players`}
    >
      <div style={{ display: 'flex', gap: 16 }}>
        {data.m1 && cell('Biggest M1 jump', data.m1, '#38bdf8')}
        {data.mSum && cell('Biggest total jump', data.mSum, '#f472b6')}
        {data.pct &&
          cell('Biggest % jump', data.pct, '#a3e635', `+${data.pct.pct.toFixed(1)} %`)}
      </div>
    </PosterCardBox>
  );
}

function TrainLeadersPosterCard({
  leaders,
  weekStart,
  weekDescription,
}: {
  leaders: AllianceHubTrainLeader[];
  weekStart: string;
  weekDescription: string | null;
}) {
  const week = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart + 'T00:00:00Z');
    const iso = new Date(d.getTime() + i * 86400_000).toISOString().slice(0, 10);
    return { iso, leader: leaders.find((l) => l.date === iso) ?? null };
  });
  return (
    <PosterCardBox accent="#f59e0b" label="Train leaders · this week">
      {weekDescription && weekDescription.trim() !== '' && (
        <div style={{ fontSize: 13, color: '#fcd34d', fontStyle: 'italic' }}>
          {weekDescription}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 8,
          marginTop: 6,
        }}
      >
        {week.map((d, i) => (
          <div
            key={d.iso}
            style={{
              border: '1px solid rgba(245, 158, 11, 0.25)',
              background: d.leader
                ? 'rgba(245, 158, 11, 0.10)'
                : 'rgba(15, 23, 42, 0.4)',
              borderRadius: 10,
              padding: '10px 6px',
              textAlign: 'center',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div
              style={{
                fontSize: 10,
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: '#fbbf24',
                fontWeight: 700,
              }}
            >
              {DAY_LABELS[i]}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#fef3c7',
                overflowWrap: 'anywhere',
                lineHeight: 1.15,
                minHeight: 32,
              }}
            >
              {d.leader ? playerLabel(d.leader.player) : '—'}
            </div>
          </div>
        ))}
      </div>
    </PosterCardBox>
  );
}

// -------------------- Roasts cards --------------------

function WorstParkerPosterCard({
  data,
}: {
  data: AllianceHubWorstParker | null;
}) {
  if (!data) {
    return (
      <PosterCardBox accent="#f43f5e" label="Worst parker">
        <div style={{ color: '#94a3b8' }}>No pick this week.</div>
      </PosterCardBox>
    );
  }
  const hasNote = data.note && data.note.trim() !== '';
  return (
    <PosterCardBox accent="#f43f5e" label="Worst parker of the week" emphasis>
      <BigName player={data.player} color="#fecdd3" />
      {hasNote ? (
        <div style={{ fontSize: 14, color: '#e2e8f0', marginTop: 4 }}>
          {data.note}
        </div>
      ) : (
        <QuipLine text={data.quip} color="#fda4af" />
      )}
    </PosterCardBox>
  );
}

function VsKindRoastPosterCard({
  data,
}: {
  data: AllianceHubVsKindRoast;
}) {
  const isPush = data.kind === 'push';
  const accent = isPush ? '#ef4444' : '#f472b6';
  const label = isPush
    ? 'Push week · biggest slacker'
    : 'Save week · overachiever';
  const numberColor = isPush ? '#fca5a5' : '#f9a8d4';
  const nameColor = isPush ? '#fecaca' : '#fbcfe8';
  return (
    <PosterCardBox accent={accent} label={label} emphasis>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <BigName player={data.player} color={nameColor} />
        <div
          style={{
            fontSize: 34,
            fontWeight: 800,
            color: numberColor,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {data.totalM.toFixed(1)} M
        </div>
      </div>
      <QuipLine text={data.quip} color={numberColor} />
    </PosterCardBox>
  );
}

function SleepingBeautyPosterCard({
  data,
}: {
  data: AllianceHubSleepingBeauty | null;
}) {
  if (!data || data.top.length === 0) {
    return (
      <PosterCardBox accent="#facc15" label="Sleeping beauties">
        <div style={{ color: '#94a3b8' }}>Everyone is awake.</div>
      </PosterCardBox>
    );
  }
  const [leader, ...rest] = data.top;
  return (
    <PosterCardBox accent="#facc15" label="Longest without a self-report" emphasis>
      <BigName player={leader.player} color="#fef08a" />
      <div
        style={{
          fontSize: 36,
          fontWeight: 800,
          color: '#fde047',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {leader.days} days
      </div>
      <QuipLine text={leader.quip} color="#fde68a" />
      {rest.length > 0 && (
        <div style={{ fontSize: 13, color: '#fde68a', marginTop: 4 }}>
          Runners-up:{' '}
          {rest
            .map((r) => `${playerLabel(r.player)} (${r.days}d)`)
            .join(' · ')}
        </div>
      )}
    </PosterCardBox>
  );
}

function VsWeeklySlackersPosterCard({
  data,
}: {
  data: AllianceHubVsWeeklySlackers | null;
}) {
  if (
    !data ||
    (data.players.length === 0 && data.daysBelowTop.length === 0)
  ) {
    return (
      <PosterCardBox accent="#f97316" label="Weekly VS-Slackers">
        <div style={{ color: '#94a3b8' }}>
          Everyone hit the weekly target. Rare sight.
        </div>
      </PosterCardBox>
    );
  }
  return (
    <PosterCardBox
      accent="#f97316"
      label={`Below ${data.weeklyTargetM.toFixed(1)} M · week of ${data.weekStart} · ${data.daysPlayed}/6 days played`}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
        <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: '#fdba74',
              fontWeight: 700,
            }}
          >
            Missed the weekly {data.weeklyTargetM.toFixed(1)} M target
          </div>
          {data.players.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>
              Everyone hit the target.
            </div>
          ) : (
            data.players.slice(0, 6).map((s) => (
              <div
                key={s.player.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 12,
                  padding: '6px 0',
                  borderBottom: '1px solid rgba(249, 115, 22, 0.15)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {playerLabel(s.player)}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: '#fdba74',
                      fontStyle: 'italic',
                    }}
                  >
                    …{s.quip}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: '#fdba74',
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'right',
                  }}
                >
                  {s.totalM.toFixed(1)} M
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                    {s.daysBelow}/{data.daysPlayed} days below 7.2 M
                  </div>
                </div>
              </div>
            ))
          )}
          {data.players.length > 6 && (
            <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>
              +{data.players.length - 6} more
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: '#fdba74',
              fontWeight: 700,
            }}
          >
            Daily-Slacker highscore · days below 7.2 M
          </div>
          {data.daysBelowTop.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>
              Nothing to roast here.
            </div>
          ) : (
            data.daysBelowTop.map((r, i) => (
              <div
                key={r.player.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 8,
                  fontSize: 14,
                }}
              >
                <span style={{ color: '#f1f5f9', overflowWrap: 'anywhere' }}>
                  <span style={{ color: '#64748b', marginRight: 6 }}>
                    {i + 1}.
                  </span>
                  {playerLabel(r.player)}
                </span>
                <span
                  style={{
                    color: '#fdba74',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.daysBelow}× · {r.totalM.toFixed(1)} M
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </PosterCardBox>
  );
}

function ChatBanHofPosterCard({
  data,
}: {
  data: AllianceHubHighscore;
}) {
  if (data.count.length === 0 && data.totalHours.length === 0) return null;
  const col = (
    title: string,
    rows: React.ReactNode,
  ) => (
    <div
      style={{
        flex: 1,
        background: 'rgba(15, 23, 42, 0.6)',
        border: '2px solid rgba(148, 163, 184, 0.15)',
        borderRadius: 16,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: '#e879f9',
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'grid', gap: 4, marginTop: 6 }}>{rows}</div>
    </div>
  );
  return (
    <PosterCardBox accent="#e879f9" label="Chat-ban hall of fame">
      <div style={{ display: 'flex', gap: 16 }}>
        {col(
          'Most bans',
          data.count.slice(0, 5).map((r, i) => (
            <div
              key={r.player.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 15,
              }}
            >
              <span style={{ color: '#f1f5f9' }}>
                <span style={{ color: '#64748b', marginRight: 6 }}>
                  {i + 1}.
                </span>
                {playerLabel(r.player)}
              </span>
              <span
                style={{
                  color: '#f0abfc',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {r.count}×
              </span>
            </div>
          )),
        )}
        {col(
          'Longest total silence',
          data.totalHours.slice(0, 5).map((r, i) => (
            <div
              key={r.player.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 15,
              }}
            >
              <span style={{ color: '#f1f5f9' }}>
                <span style={{ color: '#64748b', marginRight: 6 }}>
                  {i + 1}.
                </span>
                {playerLabel(r.player)}
              </span>
              <span
                style={{
                  color: '#f0abfc',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatHours(r.totalHours)}
              </span>
            </div>
          )),
        )}
      </div>
    </PosterCardBox>
  );
}
